import React from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { Button } from 'primereact/button';
import { ProgramWithStudentStatus, CourseWithGrades } from '../../../../types';
import { toast } from 'react-toastify';
import { CourseStatus } from '../../../../../../../../api/types/programs/course/course-status';

interface TableCoursesViewProps {
  program: ProgramWithStudentStatus;
  isProgramExpanded: (programId: number) => boolean;
  openGradeDialog: (course: CourseWithGrades) => void;
  isGradeDetailsExpanded: (programId: number, courseId: number) => boolean;
  setExpandedGradeDetails: React.Dispatch<
    React.SetStateAction<Record<string, boolean>>
  >;
}

export const TableCoursesView: React.FC<TableCoursesViewProps> = ({
  program,
  isProgramExpanded,
  openGradeDialog,
  isGradeDetailsExpanded,
  setExpandedGradeDetails,
}) => {
  const statusBodyTemplate = (status: string) => {
    const getStatusSeverity = (status: string) => {
      switch (status) {
        case CourseStatus.INIT:
          return 'info';
        case CourseStatus.CLOSED:
          return 'danger';
        case CourseStatus.FINISHED:
          return 'success';
        case CourseStatus.ACTIVE:
          return 'warning';

        /*case 'לומד':
          return 'success';
        case 'נרשם':
          return 'info';
        case 'ביטל':
          return 'danger';
        case 'עזב':
          return 'warning';
        case 'סיים':
          return 'success';
        case 'דופלם':
          return 'info';
        default:
          return 'warning';*/
      }
    };

    const getStatusValue = (status: string) => {
      switch (status) {
        case CourseStatus.INIT:
          return 'טרם התחיל';
        case CourseStatus.CLOSED:
          return 'נסגר';
        case CourseStatus.FINISHED:
          return 'הסתיים';
        case CourseStatus.ACTIVE:
          return 'פעיל';
      }
    };

    const getStatusIcon = (status: string) => {
      switch (status) {
        case 'לומד':
          return 'pi pi-user';
        case 'נרשם':
          return 'pi pi-check-circle';
        case 'ביטל':
          return 'pi pi-times-circle';
        case 'עזב':
          return 'pi pi-sign-out';
        case 'סיים':
          return 'pi pi-check-square';
        case 'דופלם':
          return 'pi pi-star';
        default:
          return 'pi pi-question';
      }
    };

    return (
      <Tag
        value={getStatusValue(status)}
        severity={getStatusSeverity(status)}
        /*icon={getStatusIcon(status)}*/
        className="program-status-tag"
      />
    );
  };

  const gradeTemplate = (value: number) => {
    return <span className="grade-value">{value}</span>;
  };

  const renderGradeComponents = (course: CourseWithGrades) => {
    if (!isGradeDetailsExpanded(program.id, course.id)) {
      return null;
    }

    return (
      <div className="grade-components-container">
        <div className="grade-components-header">
          <span className="grade-components-title">מרכיבי ציון</span>
        </div>
        <div className="grade-components-list">
          {course.gradeComponents.map((component) => (
            <div key={component.id} className="grade-component-item">
              <div className="component-type">
                {component.type === 'assignment' ? 'מטלה' : 'מבחן'}
              </div>
              <div className="component-name">{component.name}</div>
              <div className="component-weight">{component.weight}%</div>
              <div className="component-score">
                {component.score ? gradeTemplate(component.score) : '-'}
              </div>
            </div>
          ))}
        </div>
        <div className="grade-components-summary">
          <div className="summary-label">ציון סופי:</div>
          <div className="summary-value grade-final">{course.finalScore}</div>
        </div>
      </div>
    );
  };

  return (
    <div
      className={`program-courses ${isProgramExpanded(program.id) ? 'expanded' : 'collapsed'}`}
    >
      <DataTable
        value={program.courses}
        responsiveLayout="scroll"
        className="courses-table"
        showGridlines
        dir="rtl"
        expandedRows={program.courses.filter((course) =>
          isGradeDetailsExpanded(program.id, course.id),
        )}
        onRowToggle={(e) => {
          const expandedCourses = e.data as CourseWithGrades[];
          const newExpandedGradeDetails: Record<string, boolean> = {};

          program.courses.forEach((course) => {
            const key = `${program.id}-${course.id}`;
            const isExpanded = expandedCourses.some((c) => c.id === course.id);
            newExpandedGradeDetails[key] = isExpanded;
          });

          setExpandedGradeDetails((prev) => ({
            ...prev,
            ...newExpandedGradeDetails,
          }));
        }}
        rowExpansionTemplate={(course) => renderGradeComponents(course)}
        onRowClick={(e) => openGradeDialog(e.data as CourseWithGrades)}
      >
        <Column expander style={{ width: '3rem' }} />
        <Column field="courseName" header="שם הקורס" sortable />
        <Column field="additionalCourseName" header="שם נוסף" sortable />
        <Column
          field="status"
          header="סטטוס קורס"
          body={(rowData: CourseWithGrades) =>
            statusBodyTemplate(rowData.status)
          }
          sortable
        />
        {/*        <Column
          field="courseHours"
          header="כמות שעות"
          dataType="numeric"
          sortable
        />*/}
        {/*<Column
          field="capacityHours"
          header="מכסת שעות"
          dataType="numeric"
          sortable
        />*/}
        {/*        <Column
          field="attendanceCount"
          header="נוכחות"
          body={(rowData) => `${rowData.attendanceCount} שיעורים`}
          sortable
          dataType="numeric"
        />*/}
        <Column
          field="attendancePercentage"
          header="אחוז נוכחות חובה בקורס"
          body={(rowData) => `${rowData.attendancePercentage}%`}
          sortable
          dataType="numeric"
        />
        {/*<Column
          field="programAttendancePercentage"
          header="נוכחות יחסית בתוכנית"
          body={() => `${program.programAttendancePercentage}%`}
          sortable
          dataType="numeric"
        />*/}

        <Column field="finalGrade" header="ציון סופי בקורס" />
        <Column
          body={(rowData) => (
            <Button
              icon="pi pi-chart-pie"
              className="p-button-rounded p-button-info p-button-text"
              onClick={(e) => {
                e.stopPropagation();
                openGradeDialog(rowData);
              }}
              tooltip="פירוט ציונים"
              tooltipOptions={{ position: 'top' }}
            />
          )}
          style={{ width: '4rem' }}
        />
      </DataTable>
    </div>
  );
};
